
import { ref, watch, onMounted } from "vue";
import { userQueryPageList } from "@/api/api";

interface User {
  id: number | string;
  name: string;
}

export default {
  name: "UserSelect",
  props: {
    modelValue: {
      type: [String, Number],
      default: undefined,
    },
    placeholder: {
      type: String,
      default: "请选择用户",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    pageSize: {
      type: Number,
      default: 5,
    },
    allowAll: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "change"],
  setup(props, { emit }) {
    const userList = ref<User[]>([]);
    const loading = ref(false);
    const pageNo = ref(1);
    const total = ref(0);
    const searchKeyword = ref("");
    
    // 计算属性用于 v-model

    // 获取用户列表（分页）
    const getUserPageList = (reset = false) => {
      if (reset) {
        pageNo.value = 1;
        userList.value = [];
      }

      loading.value = true;

      // 判断搜索关键词是纯数字还是文字
      const isNumeric = /^\d+$/.test(searchKeyword.value);
      const searchParams: any = {
        pageNo: pageNo.value,
        pageSize: props.pageSize,
      };

      // 如果是纯数字，按userId搜索；否则按name搜索
      if (searchKeyword.value) {
        if (isNumeric) {
          searchParams.id = searchKeyword.value;
        } else {
          searchParams.name = searchKeyword.value;
        }
      }

      return userQueryPageList(searchParams)
        .then((res) => {
          loading.value = false;
          if (res.code === "200" || res.data) {
            total.value = res.data.total || 0;
            // 如果是重置，直接赋值；否则追加
            if (reset) {
              userList.value = res.data.records || [];
            } else {
              userList.value = [
                ...userList.value,
                ...(res.data.records || []),
              ];
            }
          }
          return res;
        })
        .catch((err) => {
          loading.value = false;
          return Promise.reject(err);
        });
    };

    // 搜索用户（输入就搜索，清空显示全部）
    const handleSearch = (value: string) => {
      searchKeyword.value = value || "";
      getUserPageList(true);
    };

    // 用户下拉滚动加载更多
    const handleScroll = (e: any) => {
      const { target } = e;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
        if (userList.value.length < total.value && !loading.value) {
          pageNo.value += 1;
          getUserPageList(false);
        }
      }
    };

    // 用户下拉框获得焦点时
    const handleFocus = () => {
      // 如果下拉列表为空，加载第一页数据
      if (userList.value.length === 0) {
        searchKeyword.value = "";
        getUserPageList(true);
      }
    };

    // 清空用户选择时重新加载全部数据
    const handleClear = () => {
      searchKeyword.value = "";
      getUserPageList(true);
      handleChange(undefined);
    };

    // 处理选择变化
    const handleChange = (value: any) => {
      // 如果值是空字符串，转换为 null
      const finalValue = value === "" ? null : value;
      emit("update:modelValue", finalValue);
      emit("change", finalValue);
    };

    // 初始化加载当前用户（用于编辑时回显）
    const initCurrentUser = () => {
      if (props.modelValue) {
        // 先按ID搜索，确保当前用户在下拉列表中
        searchKeyword.value = String(props.modelValue);
        getUserPageList(true).then(() => {
          // 搜索后，如果当前用户不在列表中，再加载全部数据
          const userExists = userList.value.some(
            (u: User) => u.id === props.modelValue
          );
          if (!userExists) {
            // 当前用户不在搜索结果中，加载全部数据
            searchKeyword.value = "";
            getUserPageList(true);
          }
        });
      }
    };

    // 监听 modelValue 变化，如果变化了且不在列表中，需要加载
    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal && !userList.value.some((u: User) => u.id === newVal)) {
          initCurrentUser();
        }
      }
    );

    onMounted(() => {
      // 如果有初始值，初始化加载
      if (props.modelValue) {
        initCurrentUser();
      }
    });

    return {
      props,
      userList,
      loading,
      handleSearch,
      handleScroll,
      handleFocus,
      handleClear,
      handleChange,
    };
  },
};
